import { Navigate, Route, Routes } from 'react-router-dom';
import { LoginScreen, PrivateRoute, WriteRoute, LegacySlotRedirect } from './auth';
import { DiagnosticsBridge, RouteBreadcrumbs } from './diagnostics';
import { AppChrome } from './components/AppChrome';
import { GalleryView } from './gallery';
import { ViewerRoute } from './viewer';
import { ExifToolRoute, ExifPublishRoute } from './exif';
import { DedupRoute } from './dedup';
import { TaxonomyRoute } from './taxonomy';
import { AdminRoute } from './admin';
import { SettingsRoute } from './settings';
import { PublicShareRoute } from './publicshare';
import { useDocumentTitle } from './lib/useDocumentTitle';

/**
 * Arbre de routes — « TOUT DANS L'URL » (fondation routing).
 *
 * Chaque page ET chaque image est adressable : copier-coller la barre d'adresse
 * retombe sur la vue. Les URLs sont PROPRES (le slot du compte actif reste local au
 * navigateur ; les anciens liens `/u/:slot/*` sont redirigés).
 *
 * - `/login`     → écran de connexion (premier login ou ajout de compte).
 * - `/u/:slot/*` → pose le slot puis redirige vers l'URL propre.
 * - Zone authentifiée (gardée par `PrivateRoute`) :
 *     `AppChrome` = LAYOUT PARTAGÉ → en-tête PERSISTANT (fil d'Ariane + icônes de
 *     nav + cloche + menu de compte) au-dessus de TOUS les écrans, chacun rendu
 *     dans la zone `main` via `<Outlet>` :
 *       - `GalleryView` = sous-layout galerie (reste monté ; la VISIONNEUSE
 *         `/photo/:id` s'ouvre en surcouche via son propre `<Outlet>`) :
 *           `/`          → galerie (onglet Galerie)
 *           `/dossiers`  → onglet Dossiers (`?dossier=`)
 *           `/tags`      → onglet Tags (`?tag=`)
 *           `/photo/:id` → visionneuse (adressable à froid)
 *       - `/outils/exif` → Nettoyage EXIF (effacement, PAGE)
 *       - `/outils/exif-publier` → Publier des tags vers EXIF (PAGE)
 *       - `/outils/dedup` → Dédoublonnage (PAGE, sous l'en-tête)
 *       - `/outils/taxonomie` → Gestion de la taxonomie (PAGE, T5 lot B)
 *       - `/admin` → `/admin/acces` ; `/admin/:section` → Admin de GALERIE (PAGE :
 *         Accès / Erreurs de fichiers / Poubelle / Maintenance)
 *       - `/superadmin` → `/superadmin/comptes` ; `/superadmin/:section` → console
 *         SUPERADMIN (PAGE : Comptes / Journaux / Configuration LLM / Journal des
 *         appels IA — superadmin uniquement)
 *       - `/parametres` → `/parametres/compte` ; `/parametres/:section` → Paramètres (PAGE)
 * - `*` → repli vers l'accueil.
 *
 * Paramètres / Admin / Outil EXIF sont de vraies PAGES routées sous l'en-tête
 * persistant (le fil d'Ariane reste visible et pilote le retour). Seule la modale
 * « Signaler un bug » reste une modale (dialogue court, non routée).
 */
export function App(): React.JSX.Element {
  // Titre d'onglet piloté par le nom du site (LOT 3) — via `/api/health` (public), donc
  // correct dès l'écran de login. Monté ici (racine des routes) pour couvrir tout l'arbre.
  useDocumentTitle();
  return (
    <>
      {/* Diagnostic (« Signaler un bug ») : publication du contexte + capture des
          navigations. Ne rend rien, monté sous le routeur et l'auth. */}
      <DiagnosticsBridge />
      <RouteBreadcrumbs />
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/u/:slot/*" element={<LegacySlotRedirect />} />

        {/* PARTAGE PUBLIC (XPHOTO-31) : frère de `/login`, JAMAIS sous `PrivateRoute`
            ni sous `AppChrome`. Le placement est le mécanisme — une fonctionnalité
            ajoutée demain à la coquille de l'application ne peut pas atterrir sur une
            page servie à des inconnus par simple héritage. `:n` est le RANG (1-based)
            du média dans l'ensemble, pas son identifiant : un rang ne dit rien de la
            bibliothèque. */}
        <Route path="/p/:token" element={<PublicShareRoute />} />
        <Route path="/p/:token/:n" element={<PublicShareRoute />} />

        {/* Zone authentifiée : en-tête persistant (AppChrome) au-dessus de tout. */}
        <Route
          element={
            <PrivateRoute>
              <AppChrome />
            </PrivateRoute>
          }
        >
          {/* Galerie + visionneuse : GalleryView est un sous-layout (Outlet interne). */}
          <Route element={<GalleryView />}>
            <Route path="/" element={null} />
            <Route path="/dossiers" element={null} />
            <Route path="/tags" element={null} />
            <Route path="/photo/:id" element={<ViewerRoute />} />
          </Route>

          {/* Écrans convertis en PAGES (rendus dans `main`, sous l'en-tête). Les OUTILS
              d'ÉCRITURE sont gardés par `WriteRoute` (défense en profondeur : un lecteur
              atteignant l'URL directement est redirigé vers l'accueil). */}
          <Route
            path="/outils/exif"
            element={
              <WriteRoute>
                <ExifToolRoute />
              </WriteRoute>
            }
          />
          <Route
            path="/outils/exif-publier"
            element={
              <WriteRoute>
                <ExifPublishRoute />
              </WriteRoute>
            }
          />
          <Route
            path="/outils/dedup"
            element={
              <WriteRoute>
                <DedupRoute />
              </WriteRoute>
            }
          />
          <Route
            path="/outils/taxonomie"
            element={
              <WriteRoute>
                <TaxonomyRoute />
              </WriteRoute>
            }
          />
          <Route path="/admin" element={<Navigate to="/admin/acces" replace />} />
          <Route path="/admin/:section" element={<AdminRoute scope="gallery" />} />
          <Route path="/superadmin" element={<Navigate to="/superadmin/comptes" replace />} />
          <Route path="/superadmin/:section" element={<AdminRoute scope="system" />} />
          <Route path="/parametres" element={<Navigate to="/parametres/compte" replace />} />
          <Route path="/parametres/:section" element={<SettingsRoute />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
